
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'; // Import useRef
import parse from 'html-react-parser';
import useGlobalStore from '../../store/globalStore';
import './GridGallery.css';

// --- Import Button Images ---
import prevButtonImage from '../../assets/UI/button_arrow_back.svg'; // Adjust path if needed
import nextButtonImage from '../../assets/UI/button_arrow_next.svg'; // Adjust path if needed
import closeButtonImage from '../../assets/UI/button_close.svg'; // Import close image
import infoButtonImage from '../../assets/UI/button_info.svg';
import qrButtonImage from '../../assets/UI/button_QR_scan.svg';
import filterButtonImage from '../../assets/UI/button_filter.svg'; // Import filter image
import filterInverseButtonImage from '../../assets/UI/button_filter_white.svg';
import thumbnailDefaultImage from '../../assets/about_thumb.png'; // Import close image

import storyIconImage from '../../assets/icon_stories.png';
import videoIconImage from '../../assets/icon_video.png';
import logoImage from '../../assets/UI/logo_new.png';
import logoSmallImage from '../../assets/UI/logo_mobile.png';

// --- Configuration Constant ---
const ENABLE_AUTO_PAN_BY_DEFAULT = false; // Set to true to enable auto-panning on load

const GridGallery = ({ slides, onImageClick, initialFilter, viewMode }) => {
  const [selectedTheme, setSelectedTheme] = useState(initialFilter || null);
  const isElectron = useGlobalStore((state) => state.isElectron);
  const isDesktop = useGlobalStore((state) => state.isDesktop);
  const gridContainerRef = useRef(null); // Ref for the scrollable container
  const nadaRef = useRef(null); // ref for empty button to reset tabindex
  const [isAutoPanning, setIsAutoPanning] = useState(ENABLE_AUTO_PAN_BY_DEFAULT); // Use constant for default
  const [autoPanDirection, setAutoPanDirection] = useState('forward'); // 'forward' or 'backward

  const autoPanRestartTimerRef = useRef(null); // Ref for the restart timer
  const [canScrollGrid, setCanScrollGrid] = useState(false); // State to track if grid is scrollable
  const [isAtGridStart, setIsAtGridStart] = useState(true); // State to track if at the beginning
  const [isAtGridEnd, setIsAtGridEnd] = useState(false);    // State to track if at the end
  const debounceScrollTimerRef = useRef(null); // Ref for the scroll debounce timer
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false); // State for theme menu visibility
  const [hasIconOverlays, setHasIconOverlays] = useState(false);
  const [selectedGridIndex, setSelectedGridIndex] = useState(-1);

  const screensaverScrollResetTimerRef = useRef(null); // To manage the scroll reset timeout
  const showScreensaver = useGlobalStore((state) => state.showScreensaver); // Get global screensaver state
  const exhibitionTitle = useGlobalStore((state) => state.exhibitionTitle);

  
   // --- Effect to react to global screensaver state changes ---
  useEffect(() => {
    if(!isElectron) return;
    
    console.log(`[GridGallery ScreensaverEffect] Global showScreensaver changed to: ${showScreensaver}`);
    // Clear any pending scroll reset timeout from a previous state
    if (screensaverScrollResetTimerRef.current) {
      clearTimeout(screensaverScrollResetTimerRef.current);
      screensaverScrollResetTimerRef.current = null;
    }

    if (showScreensaver) {
      console.log("[GridGallery ScreensaverEffect] Screensaver is active. Pausing auto-pan.");
      pauseAutoPan(false); // Stop auto-panning and clear any restart timers

      // hack to reset the scroll position transitionwhy the screensaver is showing
      screensaverScrollResetTimerRef.current = setTimeout(() => {
        console.log("1 second delay to reset the scroll position.");
        const container = gridContainerRef.current;
        if (container) {
          container.scrollLeft = 0;
        } 
      }, 1000); // 1 second delay

    } else {
      // Only resume if it was previously active (screensaver was on) and is now inactive
      // if (prevShowScreensaverRef.current && !showScreensaver) {
      //   console.log("[GridGallery ScreensaverEffect] Screensaver became inactive. Requesting auto-pan resume (delayed).");
      //   // Use pauseAutoPanTemporarily with force=true to ensure it tries to restart after the delay
      //   pauseAutoPanTemporarily(true);
      // }
      pauseAutoPanTemporarily(true);
      // ALSO need to reset the scroll position to 0 here- NB its 'animating' due to the smooth scrolling added in css
      //window.scrollTo(0, 0)
      const container = gridContainerRef.current;
      if (container) {
        container.scrollLeft = 0;
      } 
    }
    // Update the ref to the current value for the next render
    //prevShowScreensaverRef.current = showScreensaver;
    //, pauseAutoPan, pauseAutoPanTemporarily
    
    // Cleanup the timeout when the effect re-runs or component unmounts
    return () => {
      if (screensaverScrollResetTimerRef.current) {
        clearTimeout(screensaverScrollResetTimerRef.current);
        screensaverScrollResetTimerRef.current = null;
      }
    };

  }, [showScreensaver, isElectron]);


  useEffect(() => {
    if (initialFilter) {
      console.log("Syncing selectedTheme state with initialFilter prop:", initialFilter);
      setSelectedTheme(initialFilter);
    }

    // Set initial auto-panning state based on isElectron
    console.log("ENABLE_AUTO_PAN_BY_DEFAULT", ENABLE_AUTO_PAN_BY_DEFAULT, "isElectron", isElectron );
    const effectiveAutoPanDefault = isElectron || ENABLE_AUTO_PAN_BY_DEFAULT;
    if (isAutoPanning !== effectiveAutoPanDefault) {
      //setIsAutoPanning(effectiveAutoPanDefault);
      // when we enable auto pan- make sure it doesnt scroll straight away eg. begin 5 second timer
      if(effectiveAutoPanDefault) {
        console.log("effectiveAutoPanDefault", effectiveAutoPanDefault, isAutoPanning);
        // FIXME: need the 5 second timer to delay this
        pauseAutoPanTemporarily(true);
      }
    }
  }, [initialFilter, isElectron]);

  const uniqueThemes = useMemo(() => {
    // Use a Map to store unique themes by ID, with the title as the value.
    const themesMap = new Map();
    if (slides && Array.isArray(slides)) {
      slides.forEach(slide => {
        // We need both the IDs (from .themes) and the titles (from .theme_list)
        if (slide && Array.isArray(slide.themes) && Array.isArray(slide.theme_list)) {
          slide.themes.forEach((themeId, index) => {
            // Ensure we don't add a theme if it's already in our map
            if (!themesMap.has(String(themeId))) {
              const themeTitle = slide.theme_list[index];
              if (themeId && typeof themeTitle === 'string' && themeTitle.trim() !== '') {
                themesMap.set(String(themeId), themeTitle.trim());
              }
            }
          });
        }
      });
    }
    // Convert the Map into an array of {id, title} objects
    const themesArray = Array.from(themesMap, ([id, title]) => ({ id, title }));
    console.log("Extracted Themes (with titles):", themesArray);
    return themesArray;
  }, [slides]);

  useEffect(() => {
    console.log("viewMode changed in GridGallery", viewMode, gridContainerRef.current);
    //tabIndex='0' onFocus={() => console.log('FOCUS IS ON BIG RED DIV')}  

    if(viewMode === 'grid') {
      // force focus on something here
      document.activeElement.blur();
      /*if(gridContainerRef.current) {
        console.log("focus up gridContainerRef.current");
        gridContainerRef.current.focus();        
      }*/
    } else if(viewMode === 'carousel') {
      //document.activeElement.blur();
    }
  }, [viewMode]);

  const getTabIndexGridOnly = function(index) {
    if(viewMode === 'carousel') {
      return '-1';
    }
    return String(index);
  };

  const getTabIndexFooter = function(index) {
    //return '-1';
    if(viewMode === 'carousel' || !isThemeMenuOpen) {
      //return String(index+200);
      return '-1';
    }
    return String(index+400);
  };

  const getTabIndexGridItem = function(index) {
    //console.log("getTabIndexGridItem: " , index, viewMode);
    if(viewMode === 'carousel') {
      //return String(index+200);
      return '-1';
    }
    return String(index+200);
  }; 

  const onGridItemSelected = function(index) {
    console.log("onGridItemSelected:", index, viewMode);
    setSelectedGridIndex(index);
    if(onImageClick)  onImageClick(index);
      // && onImageClick(slides.findIndex(s => s === slide))
  };

  const getSlideThumbnail = function(slide) {
    // slide.thumbnail
    if(slide.thumbnail) {
      return slide.thumbnail;
    }
    // grab the first item from the media_items
    if(slide.media_item && Array.isArray(slide.media_item) && slide.media_item.length > 0) {
      if(slide.media_item[0].thumbnail_src) return slide.media_item[0].thumbnail_src;
      //if(slide.media_item[0].thumbnail) return slide.media_item[0].thumbnail;
    }
    return null;
  };

  const filteredSlides = useMemo(() => {
    console.log("filteredSlides selectedTheme", selectedTheme);
    //console.log(slides);
    //&& !isElectron
    // update: new json data doesnt have 'type'
    /*const slidesTypeFiltered = slides.filter(slide =>
      slide && (slide.type === 'image' || slide.type === 'video' || slide.type === 'stories' || (slide.type === 'stories-about' ))
    );
    if (!selectedTheme) {
      return slidesTypeFiltered;
    }
    return slidesTypeFiltered.filter(slide =>
      (slide && (slide.type === 'stories-about' )) || // Always include 'stories-about' type
      (slide && Array.isArray(slide.theme_list) && slide.theme_list.includes(selectedTheme))
    );
    */
    if (!selectedTheme) {
      // returns ALL slides except 'stories-about
      return slides.filter(slide =>
        (slide && (slide.type !== 'stories-about' ) && (slide.type !== 'stories-qr' ) && (slide.type !== 'video-qr' ))
      )
    }
    //console.log("ok filtering by theme...");
    return slides.filter(slide =>
      (slide && Array.isArray(slide.themes) && slide.themes.map(String).includes(String(selectedTheme)))
    );
  }, [slides, selectedTheme]);

  const selectedThemeTitle = useMemo(() => {
    if (!selectedTheme) return '';
    const themeObject = uniqueThemes.find(theme => String(theme.id) === String(selectedTheme));
    return themeObject ? themeObject.title : '';
  }, [selectedTheme, uniqueThemes]);

  const handleThemeClick = (theme) => {
    console.log("Theme button clicked:", theme);
    setSelectedTheme(prevTheme => prevTheme === theme ? null : theme);
  };

  const toggleThemeMenu = () => {
    var wasThemeOpen = isThemeMenuOpen;
    setIsThemeMenuOpen(prev => !prev);
    wasThemeOpen = !wasThemeOpen;
    console.log("isThemeMenuOpen", isThemeMenuOpen, wasThemeOpen);
    if(!wasThemeOpen) {
      //document.activeElement.blur();
    }
  };

  const handleLogoClick = () => {
    window.open('https://www.unsw.edu.au/', '_blank');
  };

  const handleTitleClick = () => {
    window.open('https://www.unsw.edu.au/precincts/rhip/hth/exhibition-community', '_blank');
  };

  const handleQRClick = () => {
    console.log("QR the exhibition button clicked");
    if (slides && slides.length > 0 && onImageClick) {
      // Find the index of the first slide with type 'stories-about'
      const qrSlideIndex = slides.findIndex(slide => slide && slide.type === 'stories-qr');

      if (qrSlideIndex !== -1) {
        onImageClick(qrSlideIndex); // Trigger the found 'stories-about' slide
      } else {
        console.warn("No 'stories-qr' slide found to trigger for about action.");
      }
    } else {
      console.warn("No slides available or onImageClick handler not provided to trigger about action.");
    }
  }

  const handleAboutClick = () => {
    console.log("About the exhibition button clicked");
    if (slides && slides.length > 0 && onImageClick) {
      // Find the index of the first slide with type 'stories-about'
      // const aboutSlideIndex = slides.findIndex(slide => slide && slide.id === 'stories-about');
      const aboutSlideIndex = slides.findIndex(slide => slide && slide.id === useGlobalStore.getState().exhibitionInfoId);

      if (aboutSlideIndex !== -1) {
        onImageClick(aboutSlideIndex); // Trigger the found 'stories-about' slide
      } else {
        console.warn("No 'stories-about' slide found to trigger for about action.");
      }
    } else {
      console.warn("No slides available or onImageClick handler not provided to trigger about action.");
    }
  };

  // --- Function to get grid item width ---
  const getGridItemWidth = () => {
    if (gridContainerRef.current) {
      const firstItem = gridContainerRef.current.querySelector('.grid-item');
      if (firstItem) {
        // Use offsetWidth which includes padding and border
        return firstItem.offsetWidth;
      }
    }
    return 300; // Fallback width if calculation fails
  };

  // --- Function to pause auto-panning and set a restart timer ---
  const pauseAutoPanTemporarily = useCallback((forceAutoPan = false) => {
    // Only act if auto-panning is currently active
    if (isAutoPanning || forceAutoPan) {
      console.log("Pausing auto-pan temporarily.", ENABLE_AUTO_PAN_BY_DEFAULT);
      setIsAutoPanning(false); // Stop the panning

      // Clear any existing restart timer
      if (autoPanRestartTimerRef.current) {
        console.log("[pauseAutoPanTemporarily] Clearing existing restart timer:", autoPanRestartTimerRef.current);
        clearTimeout(autoPanRestartTimerRef.current);
        autoPanRestartTimerRef.current = null; // Explicitly nullify after clearing
      }

      // Set a new timer to restart auto-panning - Store ID locally first
      const timerId = setTimeout(() => {
        console.log("Restarting auto-pan after timeout.");
        // Check if auto-pan should be enabled (e.g., based on initial config or a toggle)
        const shouldEnableAutoPan = isElectron || ENABLE_AUTO_PAN_BY_DEFAULT;
        if (shouldEnableAutoPan) {
            console.log("ENABLED auto-pan after timer");
            setIsAutoPanning(true);
        } else {
            console.log("ENABLE_AUTO_PAN_BY_DEFAULT is false, NOT setting isAutoPanning true.");
        }
        // Clear the ref *after* the timeout logic runs, only if it's still this timer
        if (autoPanRestartTimerRef.current === timerId) {
             autoPanRestartTimerRef.current = null;
        }
      }, 5000); // 5 seconds delay
      autoPanRestartTimerRef.current = timerId; // Store the new timer ID in the ref
    }
  }, [isAutoPanning, setIsAutoPanning, autoPanRestartTimerRef, isElectron]); // Added isElectron

  // --- Function to directly toggle auto-panning state and clear any restart timer ---
  const pauseAutoPan = useCallback((toggle) => {
    console.log(`pauseAutoPan: Setting isAutoPanning to ${toggle}.`);
    setIsAutoPanning(toggle);

    // Clear any existing restart timer from pauseAutoPanTemporarily
    if (autoPanRestartTimerRef.current) {
      console.log("[pauseAutoPan] Clearing existing autoPanRestartTimerRef:", autoPanRestartTimerRef.current);
      clearTimeout(autoPanRestartTimerRef.current);
      autoPanRestartTimerRef.current = null;
    }
  }, [setIsAutoPanning, autoPanRestartTimerRef]); // Dependencies: setIsAutoPanning, autoPanRestartTimerRef


  // --- Updated click handlers ---
  const handleGridScroll = (direction) => {
    if (!gridContainerRef.current) return;
    pauseAutoPanTemporarily(); // Pause auto-panning instead of stopping permanently

    const container = gridContainerRef.current;
    const itemWidth = getGridItemWidth();
    const scrollAmount = direction === 'next' ? itemWidth : -itemWidth;

    // Only horizontal scroll needed now as buttons only show on desktop
    if (isDesktop) {
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
    // Removed the 'else' block for mobile vertical scroll
  };


  const handleGridPrevClick = () => {
    console.log("Grid Previous Clicked");
    handleGridScroll('prev');
  };

  const handleGridNextClick = () => {
    console.log("Grid Next Clicked");
    handleGridScroll('next');
  };
  // --- End Updated Handlers ---

  // --- Effect for Auto-Panning Animation ---
  useEffect(() => {
    console.log("useEffect Auto-Panning ran")
    // Only run on desktop when auto-panning is enabled, in grid view, and ref is available
    if (!isDesktop || !isAutoPanning || viewMode !== 'grid' || !gridContainerRef.current || filteredSlides.length <= 2) {
      return;
    }

    const container = gridContainerRef.current;
    let animationFrameId = null;
    const pixelsPerSecond = 15; // 0.1 pixels per frame * 60fps = 6 pixels per second
    let lastTime = performance.now();
    let accumulatedScroll = container.scrollLeft;

    const step = (timestamp) => {
      // Double-check conditions inside the loop
      if (!isAutoPanning || !container || viewMode !== 'grid') {
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        return;
      }

      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;
      
      // Calculate actual movement based on elapsed time for precise control
      const moveAmount = (pixelsPerSecond * deltaTime) / 1000;

      const { scrollWidth, clientWidth } = container;
      const maxScrollLeft = scrollWidth - clientWidth;

      if (autoPanDirection === 'forward') {
        if (accumulatedScroll >= maxScrollLeft - 0.5) {
          setAutoPanDirection('backward');
          console.log("switched direction backward", accumulatedScroll);
        } else {
          accumulatedScroll += moveAmount;
          container.scrollLeft = accumulatedScroll;

          // sometimes it's not scrolling at all?
          //console.log("accumulatedScroll", accumulatedScroll, maxScrollLeft, container.scrollLeft, isAutoPanning, autoPanDirection);
        }
      } else { // Direction is 'backward'
        if (accumulatedScroll <= 0.5) {
          setAutoPanDirection('forward');
          console.log("switched direction forward", accumulatedScroll);
        } else {
          accumulatedScroll -= moveAmount;
          container.scrollLeft = accumulatedScroll;
        }
      }

      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);

    // Cleanup function to stop animation when effect dependencies change or component unmounts
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isDesktop, isAutoPanning, autoPanDirection, gridContainerRef, viewMode, filteredSlides]); // Dependencies

  // --- Effect to check grid scrollability and current scroll position for button visibility ---
  useEffect(() => {
    const container = gridContainerRef.current;

    const updateScrollState = () => {
      if (isDesktop && container) {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const canScroll = scrollWidth > clientWidth;
        setCanScrollGrid(canScroll);
        if (canScroll) {
          setIsAtGridStart(scrollLeft <= 1); // Use a small buffer
          setIsAtGridEnd(scrollLeft >= scrollWidth - clientWidth - 1); // Use a small buffer
        } else {
          setIsAtGridStart(true);
          setIsAtGridEnd(true); // If not scrollable, it's effectively at both ends
        }
      } else {
        setCanScrollGrid(false); // Not scrollable if not desktop or no container
        setIsAtGridStart(true);
        setIsAtGridEnd(false); // Or true, depending on desired default
      }
    };

    // Check initially and whenever filteredSlides or isDesktop changes
    // Using a timeout to ensure DOM has updated after slides change (due to key prop)
    const timerId = setTimeout(updateScrollState, 50); // Small delay

    container?.addEventListener('scroll', updateScrollState, { passive: true });

    return () => {
      clearTimeout(timerId);
      container?.removeEventListener('scroll', updateScrollState);
    };
  }, [filteredSlides, isDesktop]); // Re-check when slides, desktop status, or container ref changes

  // --- Effect to Stop Auto-Panning on Manual Scroll ---
  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container || !isDesktop) return;

    const DEBOUNCE_DELAY = 150; // ms to wait after scroll stops before pausing auto-pan

    // Clear debounce timer on effect cleanup
    const cleanupTimer = () => {
        // Only clear debounce timer here
        if (debounceScrollTimerRef.current) {
            clearTimeout(debounceScrollTimerRef.current);
            debounceScrollTimerRef.current = null;
        }
    };

    const handleManualScroll = () => {
      if (isAutoPanning) {
        // console.log("Manual scroll detected, stopping auto-pan.");
        pauseAutoPanTemporarily(); // Pause auto-panning instead of stopping permanently
        // Clear the debounce timer if nav buttons are clicked while scroll debounce is pending
        if (debounceScrollTimerRef.current) {
            clearTimeout(debounceScrollTimerRef.current);
            debounceScrollTimerRef.current = null;
        }
      }
    };

    // Use 'wheel' event as 'scroll' might trigger during smooth auto-pan
    container.addEventListener('wheel', handleManualScroll, { passive: true });
    // Also listen for touch interaction if applicable on desktop touch devices
    container.addEventListener('touchstart', handleManualScroll, { passive: true });

    return () => {
      cleanupTimer(); // Clear timer on effect cleanup/unmount
      container.removeEventListener('wheel', handleManualScroll);
      container.removeEventListener('touchstart', handleManualScroll);
    };
  }, [gridContainerRef, isDesktop, pauseAutoPanTemporarily, isAutoPanning]); // Removed isAutoPanning from dependencies

  // --- Effect to clean up the restart timer ONLY on unmount ---
  useEffect(() => {
    // Return a function that will only run when the component unmounts
    return () => {
      if (autoPanRestartTimerRef.current) {
        console.log("[Unmount Cleanup] Clearing restart timer:", autoPanRestartTimerRef.current);
        clearTimeout(autoPanRestartTimerRef.current);
        autoPanRestartTimerRef.current = null;
      }
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  return (
    <div className="grid-gallery-wrapper">
      {/* Fixed Header */}
      <div className="grid-header">
        {/* <button className="nada" tabIndex='1' ref={nadaRef}>nada</button> */}
        <div className="grid-logo-container" onClick={!isElectron ? handleLogoClick : null} tabIndex={2} ref={nadaRef}><img src={ isDesktop ? logoImage : logoSmallImage} alt="UNSW logo" className="grid-logo" /></div>
        <h1 className="grid-main-title" onClick={!isElectron ? handleTitleClick : null}>{parse(exhibitionTitle || '')}</h1>

        {isElectron ?
        <button className="filter-collection-button about-header-button" onClick={handleQRClick} tabIndex={-1}>
          {isDesktop ? <>Visit online <br /> exhibition</> : <></>} <img className='info-icon' src={qrButtonImage} alt="QR exhibition info" />
        </button>
        :
        <button className="filter-collection-button about-header-button" onClick={handleAboutClick} tabIndex={3}>
          {isDesktop ? <>About this <br /> exhibition</> : <></>} <img className='info-icon' src={infoButtonImage} alt="Exhibition info" />
        </button>
        }
        
      </div>

      {/* Scrollable Grid Content Area - Add the ref here */}
      <div
        className="grid-gallery-container"
        ref={gridContainerRef}
        key={selectedTheme || 'all-slides'} // Add dynamic key here
        onFocus={() => console.log('FOCUS IS ON BIG RED DIV')}
      >
        {(!filteredSlides || filteredSlides.length === 0) ? (
          <div className="grid-empty-message">{selectedTheme ? `No items match the theme "${selectedTheme}".` : 'No items to display in grid.'}</div>
        ) : (
          filteredSlides.map((slide, index) => {
            //<div className={`theme-menu-sheet ${isThemeMenuOpen ? 'open' : ''}`}>
            // Generate a random delay for each item (e.g., between 0 and 0.5 seconds)
            const randomDelay = index * 0.03; // Math.random() * 0.5;
            return (
            <div
              key={getSlideThumbnail(slide) || index}
              className={`grid-item ${filteredSlides.length < 8 ? 'grid-item-1' : ''}`}
              style={{ animationDelay: `${randomDelay}s` }} // Apply random delay
              onClick={() => onGridItemSelected(slides.findIndex(s => s === slide))}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onGridItemSelected(slides.findIndex(s => s === slide));
              } }}
              role='button'
              tabIndex={getTabIndexGridItem(index)}
            >
              <img
                src={getSlideThumbnail(slide) || thumbnailDefaultImage} // not working electron??? its the lazy loading
                // src="../downloads/7-7_machine_gun_1_story_thumb.jpg" // works???
                alt={slide.title || `Grid image ${index + 1}`}
                className="grid-thumbnail"
                loading={isElectron ? undefined : "lazy"} // Conditionally apply lazy loading (lazy doesnt work for electron)
              />
              {/* --- Icon Overlay --- */}
              {/* {hasIconOverlays && ( */}
                {hasIconOverlays && (slide.type === 'video' || slide.type === 'stories') && (
                  <div className="grid-item-icon-overlay"></div>
                )}
                {hasIconOverlays && slide.type === 'video' && (
                  <img src={videoIconImage} alt="Video icon" className="grid-item-type-icon video-icon" />
                )}
                {hasIconOverlays && slide.type === 'stories' && (
                  <img src={storyIconImage} alt="Story icon" className="grid-item-type-icon story-icon" />
                )}
              {/* --- End Icon Overlay --- */}


              <div className="grid-item-themes-debug">
                  type: {slide.type}, id: {slide.id}, sort_id: {slide.sort_id}, themes:
                  {(slide && Array.isArray(slide.themes)) && (slide.themes.join(', '))}<br/>                  
                  {/* {filteredSlides.length} {(isElectron) ? "electron" : "web"} */}
                  
              </div>

              {/* the "about thumb" needs a custom title */}
              {(slide.type === 'stories-about') && (
                <div className="grid-item-about-title"><h2 className={`${filteredSlides.length < 8 ? '' : 'small'}`}>About this<br />collection</h2></div>)}

            </div>
            );
          })
        )}
      </div>

      {/* --- Grid Navigation Buttons (Desktop Only) --- */}
      {isDesktop && canScrollGrid && !isAtGridStart && filteredSlides.length > 2 && (
        <>
          <button
            className="grid-nav-button grid-nav-prev"
            onClick={handleGridPrevClick}
            aria-label="Previous page"
          >
            <img src={prevButtonImage} alt="Previous" />
          </button>
        </>
      )}
      {isDesktop && canScrollGrid && !isAtGridEnd && filteredSlides.length > 2 && (
        <>
          <button
            className="grid-nav-button grid-nav-next"
            onClick={handleGridNextClick}
            aria-label="Next page"
          >
            <img src={nextButtonImage} alt="Next" />
          </button>
        </>
      )}
      {/* --- End Grid Navigation Buttons --- */}

      {/* Fixed Footer - Conditionally render if not in Electron mode */}
      {!isElectron && (
        <div className="grid-footer">
          {uniqueThemes.length > 0 ? ( // Only show filter-related buttons if there are themes
            selectedTheme ? (
              <>
               <div className='filter-collection-theme-label'>{selectedThemeTitle ? `${selectedThemeTitle}` : ''}</div>
              <button
                className="filter-collection-button remove-filter-button"
                onClick={() => {
                  setSelectedTheme(null); // Clear the selected theme
                  setIsThemeMenuOpen(false); // Ensure menu is closed if it was open
                }}
                tabIndex={getTabIndexGridOnly(400)}
              >
                {isDesktop ? <>Remove filter</> : <></>} <img className='close-icon' src={closeButtonImage} alt="Remove Filter" />
              </button>
              
              </>
            ) : (
              <button className="filter-collection-button add-filter-button" onClick={toggleThemeMenu} tabIndex={getTabIndexGridOnly(400)}>
                Filter collection <img className='filter-icon' src={filterButtonImage} alt="Add Filter" />
              </button>
            )
          ) : (
            <span>{/* Placeholder if no themes, or other footer content */}</span>
          )}
        </div>
      )}

      {/* Theme Menu Sheet - Conditionally render if not in Electron mode (assuming it's tied to the footer button) */}
      {/* Theme Menu Sheet */}
      {uniqueThemes.length > 0 && (
        <div className={`theme-menu-sheet ${isThemeMenuOpen ? 'open' : ''}`}>
          <div className="theme-menu-header">
            <h4>Filter collection <img className='filter-icon-inverse' src={filterInverseButtonImage} alt="Filter collection" /></h4>
            <button onClick={toggleThemeMenu} className="close-theme-menu-button" aria-label="Close theme filter" tabIndex={getTabIndexFooter(20)}>
              {isDesktop ? <>Close</> : <></>} <img src={closeButtonImage} alt="Close" />
            </button>
          </div>
          <div className="theme-menu-content">
            {uniqueThemes.map(({ id, title }, index) => (
              <button
                key={id}
                className={`grid-theme-button ${String(selectedTheme) === String(id) ? 'selected' : ''}`}
                onClick={() => {
                  handleThemeClick(id);
                  // Optionally close menu on theme selection:
                  setIsThemeMenuOpen(false); 
                }}
                tabIndex={getTabIndexFooter(index+2)}
              >
                {title}
              </button>
            ))}
          </div>
          <div className='theme-menu-footer'>
          </div>
        </div>
      )}
    </div>
  );
};

export default GridGallery;
